import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import { LatencySummary } from "../../types/metrics";
import { callMetricsService } from "../../services/callMetricsService";

interface LatencyStatisticsProps {
  summary: LatencySummary;
}

export const LatencyStatistics: React.FC<LatencyStatisticsProps> = ({
  summary,
}) => {
  const statisticsData = [
    {
      metric: "STT Latency (Transcription Delay)",
      min: summary.stt.min,
      max: summary.stt.max,
      avg: summary.stt.avg,
      median: summary.stt.median,
      p50: summary.stt.p50,
      p75: summary.stt.p75,
      p95: summary.stt.p95,
      p99: summary.stt.p99,
      stddev: summary.stt.stddev,
      count: summary.stt.count,
    },
    ...(summary.eou?.end_of_utterance_delay && summary.eou.end_of_utterance_delay.count > 0 ? [{
      metric: "End of Utterance Detection Delay",
      min: summary.eou.end_of_utterance_delay.min,
      max: summary.eou.end_of_utterance_delay.max,
      avg: summary.eou.end_of_utterance_delay.avg,
      median: summary.eou.end_of_utterance_delay.median,
      p50: summary.eou.end_of_utterance_delay.p50,
      p75: summary.eou.end_of_utterance_delay.p75,
      p95: summary.eou.end_of_utterance_delay.p95,
      p99: summary.eou.end_of_utterance_delay.p99,
      stddev: summary.eou.end_of_utterance_delay.stddev,
      count: summary.eou.end_of_utterance_delay.count,
    }] : []),
    ...(summary.eou?.on_user_turn_completed_delay && summary.eou.on_user_turn_completed_delay.count > 0 ? [{
      metric: "User Turn Completion Delay",
      min: summary.eou.on_user_turn_completed_delay.min,
      max: summary.eou.on_user_turn_completed_delay.max,
      avg: summary.eou.on_user_turn_completed_delay.avg,
      median: summary.eou.on_user_turn_completed_delay.median,
      p50: summary.eou.on_user_turn_completed_delay.p50,
      p75: summary.eou.on_user_turn_completed_delay.p75,
      p95: summary.eou.on_user_turn_completed_delay.p95,
      p99: summary.eou.on_user_turn_completed_delay.p99,
      stddev: summary.eou.on_user_turn_completed_delay.stddev,
      count: summary.eou.on_user_turn_completed_delay.count,
    }] : []),
    {
      metric: "LLM TTFT (Time to First Token)",
      min: summary.llm.ttft.min,
      max: summary.llm.ttft.max,
      avg: summary.llm.ttft.avg,
      median: summary.llm.ttft.median,
      p50: summary.llm.ttft.p50,
      p75: summary.llm.ttft.p75,
      p95: summary.llm.ttft.p95,
      p99: summary.llm.ttft.p99,
      stddev: summary.llm.ttft.stddev,
      count: summary.llm.ttft.count,
    },
    {
      metric: "LLM Total Duration",
      min: summary.llm.duration.min,
      max: summary.llm.duration.max,
      avg: summary.llm.duration.avg,
      median: summary.llm.duration.median,
      p50: summary.llm.duration.p50,
      p75: summary.llm.duration.p75,
      p95: summary.llm.duration.p95,
      p99: summary.llm.duration.p99,
      stddev: summary.llm.duration.stddev,
      count: summary.llm.duration.count,
    },
    {
      metric: "LLM Tokens/Second",
      min: summary.llm.tokens_per_second.min,
      max: summary.llm.tokens_per_second.max,
      avg: summary.llm.tokens_per_second.avg,
      median: summary.llm.tokens_per_second.median,
      p50: summary.llm.tokens_per_second.p50,
      p75: summary.llm.tokens_per_second.p75,
      p95: summary.llm.tokens_per_second.p95,
      p99: summary.llm.tokens_per_second.p99,
      stddev: summary.llm.tokens_per_second.stddev,
      count: summary.llm.tokens_per_second.count,
      isTokenMetric: true,
    },
    {
      metric: "TTS TTFB (Time to First Byte)",
      min: summary.tts.ttfb.min,
      max: summary.tts.ttfb.max,
      avg: summary.tts.ttfb.avg,
      median: summary.tts.ttfb.median,
      p50: summary.tts.ttfb.p50,
      p75: summary.tts.ttfb.p75,
      p95: summary.tts.ttfb.p95,
      p99: summary.tts.ttfb.p99,
      stddev: summary.tts.ttfb.stddev,
      count: summary.tts.ttfb.count,
    },
    {
      metric: "TTS Total Duration",
      min: summary.tts.duration.min,
      max: summary.tts.duration.max,
      avg: summary.tts.duration.avg,
      median: summary.tts.duration.median,
      p50: summary.tts.duration.p50,
      p75: summary.tts.duration.p75,
      p95: summary.tts.duration.p95,
      p99: summary.tts.duration.p99,
      stddev: summary.tts.duration.stddev,
      count: summary.tts.duration.count,
    },
    {
      metric: "VAD Inference Duration",
      min: summary.vad.inference_duration.min,
      max: summary.vad.inference_duration.max,
      avg: summary.vad.inference_duration.avg,
      median: summary.vad.inference_duration.median,
      p50: summary.vad.inference_duration.p50,
      p75: summary.vad.inference_duration.p75,
      p95: summary.vad.inference_duration.p95,
      p99: summary.vad.inference_duration.p99,
      stddev: summary.vad.inference_duration.stddev,
      count: summary.vad.inference_duration.count,
    },
    {
      metric: "Overall Response Time",
      min: summary.overall_response_time.min,
      max: summary.overall_response_time.max,
      avg: summary.overall_response_time.avg,
      median: summary.overall_response_time.median,
      p50: summary.overall_response_time.p50,
      p75: summary.overall_response_time.p75,
      p95: summary.overall_response_time.p95,
      p99: summary.overall_response_time.p99,
      stddev: summary.overall_response_time.stddev,
      count: summary.overall_response_time.count,
    },
  ];

  const formatValue = (value: number, isTokenMetric: boolean = false) => {
    if (isTokenMetric) {
      return value.toFixed(2);
    }
    return callMetricsService.formatDuration(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Detailed Statistics</CardTitle>
        <CardDescription>
          Statistical breakdown of all latency metrics
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[250px]">Metric</TableHead>
                <TableHead className="text-right">Min</TableHead>
                <TableHead className="text-right">Max</TableHead>
                <TableHead className="text-right">Avg</TableHead>
                <TableHead className="text-right">Median</TableHead>
                <TableHead className="text-right">P50</TableHead>
                <TableHead className="text-right">P75</TableHead>
                <TableHead className="text-right">P95</TableHead>
                <TableHead className="text-right">P99</TableHead>
                <TableHead className="text-right">Std Dev</TableHead>
                <TableHead className="text-right">Count</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {statisticsData.map((row, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{row.metric}</TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatValue(row.min, row.isTokenMetric)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatValue(row.max, row.isTokenMetric)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm font-semibold">
                    {formatValue(row.avg, row.isTokenMetric)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatValue(row.median, row.isTokenMetric)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    <Badge variant="outline">
                      {formatValue(row.p50, row.isTokenMetric)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    <Badge variant="outline">
                      {formatValue(row.p75, row.isTokenMetric)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    <Badge variant="secondary">
                      {formatValue(row.p95, row.isTokenMetric)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    <Badge variant="destructive">
                      {formatValue(row.p99, row.isTokenMetric)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-muted-foreground">
                    {formatValue(row.stddev, row.isTokenMetric)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {row.count}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Additional Metrics */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                LLM Cache Efficiency
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {summary.llm.cache_hit_ratio.toFixed(1)}%
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Percentage of tokens served from cache
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                STT Audio Processed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {callMetricsService.formatDuration(summary.stt.avg_audio_duration)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Average audio duration per STT request
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                TTS Audio Generated
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {callMetricsService.formatDuration(summary.tts.avg_audio_duration)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Average audio duration per TTS request
              </p>
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>
  );
};
